import type { FunctionComponent } from 'react';
import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { stopPropagationAndPreventDefault } from 'app/src/stopPropagationAndPreventDefault';
import { memberId } from '@trello/session-cookie';
import { useUpdateBoardNameMutation } from './UpdateBoardNameMutation.generated';
import { useBoardNameQuery } from './BoardNameQuery.generated';
import { canRename as canRenameHelper } from '@trello/boards';
import { BoardHeaderTestIds } from '@trello/test-ids';
import { forTemplate } from '@trello/i18n';

import cx from 'classnames';
import styles from './BoardName.less';

const format = forTemplate('board_header');

interface BoardNameProps {
  boardId: string;
}

export const BoardName: FunctionComponent<BoardNameProps> = ({ boardId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [updateBoardName] = useUpdateBoardNameMutation();
  const { data, loading } = useBoardNameQuery({
    variables: { boardId: boardId, memberId: memberId || '' },
    fetchPolicy: 'cache-only',
    waitOn: ['MemberHeader', 'CurrentBoardFull'],
  });

  const canRename = useMemo(() => {
    if (loading || !data || !data.board || !data.member) {
      return false;
    } else {
      return canRenameHelper(
        data.member,
        data.board,
        data.board.organization || null,
        null,
      );
    }
  }, [loading, data]);

  const boardName = data?.board?.name;

  /**
   * Resizes the board name input up to the maximum width of the board header. Currently,
   * this gets the board header width via a document query, but once the board header is
   * completely in react should be done via a passed ref instead
   */
  const resizeInput = useCallback(() => {
    if (!inputRef?.current) {
      return;
    }
    inputRef.current.style.width = '0';
    let inputWidthForNewValue = inputRef.current.offsetWidth;

    inputRef.current.scrollLeft = 1e10;
    // With the width set to zero and the input scrolled to the end of the content, the element's
    // scrollLeft value is the width of the content! Now we add that to the offset.
    inputWidthForNewValue += inputRef.current.scrollLeft;

    // Here we make sure the input doesn't fall off the side of the page. The "-24" is for padding.
    const boardHeader = document.querySelector<HTMLElement>('.js-board-header');
    const headerOffsetWidth = boardHeader?.offsetWidth;
    const maxWidth = (headerOffsetWidth || 1e10) - 24;

    const inputWidth =
      inputWidthForNewValue < maxWidth ? inputWidthForNewValue : maxWidth;
    // Now we have an input that's only as wide as the content needs, without running off the page.
    inputRef.current.style.width = inputWidth + 'px';
  }, [inputRef]);

  const onStartEditing = useCallback(
    (e) => {
      stopPropagationAndPreventDefault(e);
      if (isEditing || !canRename) {
        return;
      }

      setIsEditing(true);

      Analytics.sendUIEvent({
        action: 'clicked',
        actionSubject: 'input',
        actionSubjectId: 'boardNameInput',
        source: 'boardScreen',
        containers: {
          board: {
            id: boardId,
          },
        },
      });
    },
    [isEditing, canRename, boardId],
  );

  // focus and select the input onClick. This is done here because the input ref
  // is not yet defined in the onClick of the divß
  useEffect(() => {
    if (isEditing) {
      inputRef?.current?.focus();
      inputRef?.current?.select();
    }
  }, [inputRef, isEditing]);

  const onEndEditing: React.FocusEventHandler<HTMLInputElement> = useCallback(
    async (e) => {
      if (!isEditing) {
        return;
      }

      const newBoardName = e.currentTarget.value.trim();

      if (!newBoardName || newBoardName === '') {
        inputRef.current?.focus();
        return;
      }

      setIsEditing(false);

      if (newBoardName === boardName) {
        return;
      }

      const traceId = Analytics.startTask({
        taskName: 'edit-board/name',
        source: 'boardScreen',
      });

      try {
        await updateBoardName({
          variables: {
            boardId: boardId,
            traceId: traceId,
            boardName: newBoardName,
          },
          optimisticResponse: {
            __typename: 'Mutation',
            updateBoardName: {
              __typename: 'Board',
              id: boardId,
              name: newBoardName,
            },
          },
        });

        Analytics.taskSucceeded({
          taskName: 'edit-board/name',
          source: 'boardScreen',
          traceId,
        });
      } catch (error) {
        Analytics.taskFailed({
          taskName: 'edit-board/name',
          source: 'boardScreen',
          traceId,
          // @ts-expect-error
          error,
        });
      }
    },
    [isEditing, boardName, updateBoardName, boardId],
  );

  // Ensure the element is focusable via the keyboard
  const onWrapperKeyUp: React.KeyboardEventHandler = useCallback(
    (e: React.KeyboardEvent<Element>) => {
      if (e.key === 'Enter') {
        onStartEditing(e);
      }
    },
    [onStartEditing],
  );

  // Pressing Enter submits the name change
  const onInputKeyUp: React.KeyboardEventHandler = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.currentTarget.blur();
      }
    },
    [],
  );

  return (
    <div
      className={cx(
        styles.boardNameContainer,
        canRename && styles.boardNameRenamable,
        isEditing && styles.isEditing,
      )}
      data-testid={BoardHeaderTestIds.BoardNameContainer}
      tabIndex={0}
      aria-label={format('board-name')}
      role={'textbox'}
      onKeyUp={onWrapperKeyUp}
      onClick={onStartEditing}
    >
      <input
        data-testid={BoardHeaderTestIds.BoardNameInput}
        defaultValue={boardName}
        ref={inputRef}
        onBlur={onEndEditing}
        // Make sure the initial input is correctly sized
        onFocus={resizeInput}
        // Make sure the input is resized as a user types
        onInput={resizeInput}
        className={cx(styles.boardNameInput)}
        onKeyUp={onInputKeyUp}
      />
      <h1
        className={styles.boardName}
        data-testid={BoardHeaderTestIds.BoardNameDisplay}
      >
        {boardName}
      </h1>
    </div>
  );
};
